import React, { useEffect, useMemo, useState } from 'react';
import ReactFlow, {
  Background,
  Controls,
  type Edge,
  type Node,
  type NodeProps,
  Handle,
  Position,
} from 'reactflow';
import dagre from 'dagre';
import { FaKey, FaLink } from 'react-icons/fa';

import 'reactflow/dist/style.css';
import './css/SqlSchemaDiagram.css';

type TableColumn = {
  name: string;
  rawType?: string;
  notNull?: boolean;
  isUnique?: boolean;
};

type ForeignKey = {
  fromTable: string;
  fromColumn?: string;
  toTable: string;
  toColumn?: string;
  fromNotNull?: boolean;
  fromUnique?: boolean;
};

type ParsedSchema = {
  tables: Array<{
    name: string;
    columns: TableColumn[];
    primaryKeys: string[];
  }>;
  foreignKeys: ForeignKey[];
};

function stripSqlComments(sql: string): string {
  return (sql || '')
    .replace(/--.*$/gm, '')
    .replace(/\/\*[\s\S]*?\*\//g, ' ');
}

function unquoteIdent(raw: string): string {
  const s = raw.trim();
  if (!s) return s;
  if ((s.startsWith('"') && s.endsWith('"')) || (s.startsWith('`') && s.endsWith('`'))) {
    return s.slice(1, -1);
  }
  if (s.startsWith('[') && s.endsWith(']')) return s.slice(1, -1);
  return s;
}

function splitTopLevelByComma(body: string): string[] {
  const parts: string[] = [];
  let cur = '';
  let depth = 0;
  let inSingle = false;
  let inDouble = false;
  let inBacktick = false;

  for (let i = 0; i < body.length; i++) {
    const ch = body[i];
    const prev = i > 0 ? body[i - 1] : '';

    if (!inDouble && !inBacktick && ch === "'" && prev !== '\\') inSingle = !inSingle;
    else if (!inSingle && !inBacktick && ch === '"' && prev !== '\\') inDouble = !inDouble;
    else if (!inSingle && !inDouble && ch === '`') inBacktick = !inBacktick;

    if (!inSingle && !inDouble && !inBacktick) {
      if (ch === '(') depth++;
      if (ch === ')') depth = Math.max(0, depth - 1);
    }

    if (!inSingle && !inDouble && !inBacktick && depth === 0 && ch === ',') {
      const trimmed = cur.trim();
      if (trimmed) parts.push(trimmed);
      cur = '';
      continue;
    }
    cur += ch;
  }
  const trimmed = cur.trim();
  if (trimmed) parts.push(trimmed);
  return parts;
}

function parseColumnList(raw: string): string[] {
  const inside = raw.trim().replace(/^\(/, '').replace(/\)$/, '');
  if (!inside.trim()) return [];
  return splitTopLevelByComma(inside).map((s) => {
    const m = s.trim().match(/^(?:"([^"]+)"|`([^`]+)`|\[([^\]]+)\]|(\w+))$/);
    const name = m ? (m[1] ?? m[2] ?? m[3] ?? m[4] ?? '') : s.trim();
    return unquoteIdent(name);
  }).filter(Boolean);
}

function parseSqlSchema(schemaSql: string): ParsedSchema {
  const sql = stripSqlComments(schemaSql);
  const tables: ParsedSchema['tables'] = [];
  const foreignKeys: ForeignKey[] = [];

  // CREATE TABLE [IF NOT EXISTS] [schema.]table ( ... );
  const createRe =
    /\bcreate\s+table\s+(?:if\s+not\s+exists\s+)?(?:(?:"[^"]+"|`[^`]+`|\[[^\]]+\]|\w+)\s*\.\s*)?(?:"[^"]+"|`[^`]+`|\[[^\]]+\]|\w+)\s*\(/gi;

  let m: RegExpExecArray | null;
  while ((m = createRe.exec(sql)) !== null) {
    const startIdx = m.index;
    const openParenIdx = sql.indexOf('(', startIdx);
    if (openParenIdx < 0) continue;

    // Walk to matching closing paren for this CREATE TABLE.
    let depth = 0;
    let endParenIdx = -1;
    for (let i = openParenIdx; i < sql.length; i++) {
      const ch = sql[i];
      if (ch === '(') depth++;
      else if (ch === ')') {
        depth--;
        if (depth === 0) {
          endParenIdx = i;
          break;
        }
      }
    }
    if (endParenIdx < 0) continue;

    const header = sql.slice(startIdx, openParenIdx);
    const tableNameMatch =
      header.match(
        /\bcreate\s+table\s+(?:if\s+not\s+exists\s+)?(?:(?:"([^"]+)"|`([^`]+)`|\[([^\]]+)\]|(\w+))\s*\.\s*)?(?:"([^"]+)"|`([^`]+)`|\[([^\]]+)\]|(\w+))\s*$/i
      ) ?? [];
    const tableName =
      (tableNameMatch[5] ??
        tableNameMatch[6] ??
        tableNameMatch[7] ??
        tableNameMatch[8] ??
        '') || '';
    const normalizedTableName = unquoteIdent(tableName);
    if (!normalizedTableName) continue;

    const body = sql.slice(openParenIdx + 1, endParenIdx);
    const items = splitTopLevelByComma(body);

    const columns: TableColumn[] = [];
    const primaryKeys = new Set<string>();
    const uniqueCols = new Set<string>();

    for (const it of items) {
      const lower = it.trim().toLowerCase();

      // Table-level PK: PRIMARY KEY (a, b)
      const pkTableLevel = it.match(/\bprimary\s+key\s*\(([\s\S]*?)\)\s*$/i);
      if (pkTableLevel) {
        const cols = parseColumnList(`(${pkTableLevel[1]})`);
        for (const c of cols) primaryKeys.add(c);
        continue;
      }

      // Table-level UNIQUE: UNIQUE (a, b) or CONSTRAINT name UNIQUE (a, b)
      const uniqTableLevel = it.match(/\bunique\s*\(([\s\S]*?)\)\s*$/i);
      if (uniqTableLevel && /\bunique\b/i.test(it)) {
        const cols = parseColumnList(`(${uniqTableLevel[1]})`);
        if (cols.length === 1) uniqueCols.add(cols[0]);
        continue;
      }

      // Table-level FK: FOREIGN KEY (col) REFERENCES other(col)
      const fkTableLevel = it.match(
        /\bforeign\s+key\s*\(\s*([^)]+?)\s*\)\s*references\s+(?:(?:"([^"]+)"|`([^`]+)`|\[([^\]]+)\]|(\w+))\s*\.\s*)?(?:"([^"]+)"|`([^`]+)`|\[([^\]]+)\]|(\w+))\s*(?:\(\s*([^)]+?)\s*\))?/i
      );
      if (fkTableLevel) {
        const fromCols = parseColumnList(`(${fkTableLevel[1]})`);
        const toTable = unquoteIdent(fkTableLevel[6] ?? fkTableLevel[7] ?? fkTableLevel[8] ?? fkTableLevel[9] ?? '');
        const toCols = fkTableLevel[10] ? parseColumnList(`(${fkTableLevel[10]})`) : [];
        if (toTable) {
          const max = Math.max(fromCols.length, toCols.length, 1);
          for (let i = 0; i < max; i++) {
            foreignKeys.push({
              fromTable: normalizedTableName,
              fromColumn: fromCols[i] || undefined,
              toTable,
              toColumn: toCols[i] || undefined,
            });
          }
        }
        continue;
      }

      // Skip other table constraints
      if (
        lower.startsWith('check')
      ) {
        continue;
      }

      // Column definition: name type ...
      const colMatch = it.match(/^\s*(?:"([^"]+)"|`([^`]+)`|\[([^\]]+)\]|(\w+))\s+(.+)\s*$/);
      if (!colMatch) continue;
      const colName = unquoteIdent(colMatch[1] ?? colMatch[2] ?? colMatch[3] ?? colMatch[4] ?? '');
      const rest = (colMatch[5] ?? '').trim();
      if (!colName) continue;

      // Column-level PK
      if (/\bprimary\s+key\b/i.test(rest)) {
        primaryKeys.add(colName);
      }
      // Column-level UNIQUE
      if (/\bunique\b/i.test(rest)) {
        uniqueCols.add(colName);
      }
      const notNull = /\bnot\s+null\b/i.test(rest);

      // Inline REFERENCES: col INT REFERENCES other(id)
      const inlineRef = rest.match(
        /\breferences\s+(?:(?:"([^"]+)"|`([^`]+)`|\[([^\]]+)\]|(\w+))\s*\.\s*)?(?:"([^"]+)"|`([^`]+)`|\[([^\]]+)\]|(\w+))\s*(?:\(\s*(?:"([^"]+)"|`([^`]+)`|\[([^\]]+)\]|(\w+))\s*\))?/i
      );
      if (inlineRef) {
        const toTable = unquoteIdent(inlineRef[5] ?? inlineRef[6] ?? inlineRef[7] ?? inlineRef[8] ?? '');
        const toCol = unquoteIdent(inlineRef[9] ?? inlineRef[10] ?? inlineRef[11] ?? inlineRef[12] ?? '');
        if (toTable) {
          foreignKeys.push({
            fromTable: normalizedTableName,
            fromColumn: colName,
            toTable,
            toColumn: toCol || undefined,
          });
        }
      }

      const typeOnly = rest.split(/\b(?:not|null|default|primary|unique|references|check|constraint)\b/i)[0]?.trim();
      columns.push({
        name: colName,
        rawType: typeOnly || undefined,
        notNull,
        isUnique: uniqueCols.has(colName),
      });
    }

    tables.push({ name: normalizedTableName, columns, primaryKeys: Array.from(primaryKeys) });

    // Continue scanning after this table.
    createRe.lastIndex = endParenIdx + 1;
  }

  // Deduplicate FKs
  const fkKey = (fk: ForeignKey) =>
    `${fk.fromTable}.${fk.fromColumn ?? ''}->${fk.toTable}.${fk.toColumn ?? ''}`;
  const uniqueFks = new Map<string, ForeignKey>();
  for (const fk of foreignKeys) uniqueFks.set(fkKey(fk), fk);

  // Deduplicate tables by name (keep first)
  const tableMap = new Map<string, ParsedSchema['tables'][number]>();
  for (const t of tables) {
    if (!tableMap.has(t.name)) tableMap.set(t.name, t);
  }

  const uniqueTables = Array.from(tableMap.values());

  // Enrich FK with not-null/unique info from source table
  const tableByName = new Map(uniqueTables.map((t) => [t.name, t] as const));
  const enrichedFks: ForeignKey[] = Array.from(uniqueFks.values()).map((fk) => {
    const t = tableByName.get(fk.fromTable);
    const col = fk.fromColumn ? t?.columns.find((c) => c.name === fk.fromColumn) : undefined;
    const isPk = fk.fromColumn ? (t?.primaryKeys ?? []).includes(fk.fromColumn) : false;
    return {
      ...fk,
      fromNotNull: col?.notNull,
      fromUnique: Boolean(col?.isUnique || isPk),
    };
  });

  return { tables: uniqueTables, foreignKeys: enrichedFks };
}

function layoutWithDagre(nodes: Node[], edges: Edge[], tableCount: number): { nodes: Node[]; edges: Edge[] } {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({
    rankdir: 'LR',
    nodesep: tableCount > 3 ? 55 : 70,
    ranksep: tableCount > 3 ? 90 : 110,
  });

  for (const n of nodes) {
    const w = (n.data?.width as number | undefined) ?? 240;
    const h = (n.data?.height as number | undefined) ?? 120;
    g.setNode(n.id, { width: w, height: h });
  }
  for (const e of edges) g.setEdge(e.source, e.target);

  dagre.layout(g);

  const laidOut = nodes.map((n) => {
    const p = g.node(n.id);
    return {
      ...n,
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
      position: { x: p.x - p.width / 2, y: p.y - p.height / 2 },
    };
  });

  return { nodes: laidOut, edges };
}

const TableNode: React.FC<NodeProps> = ({ data }) => {
  const name = String(data?.name ?? '');
  const columns = (data?.columns as TableColumn[] | undefined) ?? [];
  const pk = new Set<string>(((data?.primaryKeys as string[] | undefined) ?? []).map(String));
  const fk = new Set<string>(((data?.foreignKeys as string[] | undefined) ?? []).map(String));

  // Geometry for per-row handles (best-effort; keeps edges attached to specific fields).
  const ROW_H = 22;
  const HEADER_H = 44; // title area
  const BODY_PAD_TOP = 10;
  const firstRowCenter = HEADER_H + BODY_PAD_TOP + ROW_H / 2;

  const mkSourceHandleId = (col: string) => `s:${col}`;
  const mkTargetHandleId = (col: string) => `t:${col}`;

  return (
    <div className="nn-erd-node">
      {/* Fallback handles (table-level) */}
      <Handle className="nn-erd-handle" id="t:__table" type="target" position={Position.Left} />
      <Handle className="nn-erd-handle" id="s:__table" type="source" position={Position.Right} />
      <div className="nn-erd-node-title">{name}</div>
      <div className="nn-erd-node-body">
        {columns.length > 0 ? (
          columns.map((c, idx) => (
            <div
              key={c.name}
              className={`nn-erd-node-row ${pk.has(c.name) ? 'is-pk' : ''} ${fk.has(c.name) ? 'is-fk' : ''}`}
            >
              {/* Per-column handles */}
              <Handle
                className="nn-erd-handle nn-erd-handle-col"
                id={mkTargetHandleId(c.name)}
                type="target"
                position={Position.Left}
                style={{ top: firstRowCenter + idx * ROW_H }}
              />
              <Handle
                className="nn-erd-handle nn-erd-handle-col"
                id={mkSourceHandleId(c.name)}
                type="source"
                position={Position.Right}
                style={{ top: firstRowCenter + idx * ROW_H }}
              />
              <span className="nn-erd-node-col">
                <span className="nn-erd-node-icons">
                  {pk.has(c.name) && (
                    <span className="nn-erd-node-icon pk" title="Primary key" aria-label="Primary key">
                      <FaKey size={12} />
                    </span>
                  )}
                  {fk.has(c.name) && (
                    <span className="nn-erd-node-icon fk" title="Foreign key" aria-label="Foreign key">
                      <FaLink size={12} />
                    </span>
                  )}
                </span>
                <span className="nn-erd-node-col-name">{c.name}</span>
              </span>
              <span className="nn-erd-node-type">{c.rawType ?? ''}</span>
            </div>
          ))
        ) : (
          <div className="nn-erd-node-empty">—</div>
        )}
      </div>
    </div>
  );
};

const nodeTypes = { table: TableNode };

export function SqlSchemaDiagram(props: { schemaSql: string; className?: string }) {
  const { schemaSql, className } = props;
  const parsed = useMemo(() => parseSqlSchema(schemaSql), [schemaSql]);

  const initial = useMemo(() => {
    const fkByTable = new Map<string, Set<string>>();
    for (const rel of parsed.foreignKeys) {
      if (!rel.fromColumn) continue;
      if (!fkByTable.has(rel.fromTable)) fkByTable.set(rel.fromTable, new Set());
      fkByTable.get(rel.fromTable)!.add(rel.fromColumn);
    }

    const baseNodes: Node[] = parsed.tables.map((t) => {
      const rowCount = t.columns.length;
      const height = Math.min(420, 56 + Math.max(1, rowCount) * 22);
      return {
        id: t.name,
        type: 'table',
        position: { x: 0, y: 0 },
        data: {
          name: t.name,
          columns: t.columns,
          primaryKeys: t.primaryKeys,
          foreignKeys: Array.from(fkByTable.get(t.name) ?? []),
          width: 280,
          height,
        },
      };
    });

    const baseEdges: Edge[] = parsed.foreignKeys
      .filter((fk) => parsed.tables.some((t) => t.name === fk.fromTable) && parsed.tables.some((t) => t.name === fk.toTable))
      .map((fk, idx) => ({
        id: `e_${idx}_${fk.fromTable}_${fk.toTable}`,
        source: fk.fromTable,
        target: fk.toTable,
        sourceHandle: fk.fromColumn ? `s:${fk.fromColumn}` : 's:__table',
        targetHandle: fk.toColumn ? `t:${fk.toColumn}` : 't:__table',
        animated: false,
        type: 'smoothstep',
        style: { strokeWidth: 2, stroke: 'rgba(232, 232, 232, 0.55)' },
      }));

    return layoutWithDagre(baseNodes, baseEdges, parsed.tables.length);
  }, [parsed]);

  const diagramHeight = parsed.tables.length > 3 ? 680 : 520;

  const [nodes, setNodes] = useState<Node[]>(initial.nodes);
  const [edges, setEdges] = useState<Edge[]>(initial.edges);

  useEffect(() => {
    setNodes(initial.nodes);
    setEdges(initial.edges);
  }, [initial.nodes, initial.edges]);

  if (!schemaSql || !schemaSql.trim()) {
    return <div className={className ?? ''}>—</div>;
  }

  return (
    <div className={`nn-erd ${className ?? ''}`} style={{ height: diagramHeight }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        proOptions={{ hideAttribution: true }}
        nodesDraggable
        nodesConnectable={false}
        elementsSelectable
      >
        <Background gap={18} size={1} />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  );
}

